import { z } from "zod";
import {
  CapabilityModelSchema,
  OperationSchema,
  ParameterSchema,
  RequestBodySchema,
  ResponseSchema,
  type CapabilityModel,
  type Operation,
  type Parameter
} from "@keystone/core";

/**
 * OpenAPI 3.x ingest. We parse a *resolved* OpenAPI document (caller is responsible for
 * dereferencing $refs — we recommend `@apidevtools/swagger-parser` `dereference()` —
 * because we do not want to ship a JSON Schema $ref walker here).
 *
 * The result is a CapabilityModel: the protocol-neutral list of operations the API offers.
 */

const HTTP_METHODS = ["get", "post", "put", "patch", "delete", "head", "options"] as const;
type Method = (typeof HTTP_METHODS)[number];

const OpenApiInfoSchema = z.object({
  title: z.string(),
  version: z.string()
});

const OpenApiServerSchema = z.object({
  url: z.string(),
  description: z.string().optional()
});

const OpenApiTagSchema = z.object({
  name: z.string(),
  description: z.string().optional()
});

/**
 * Top-level OpenAPI document. We *passthrough* unknown keys because vendors carry
 * meaningful information in `x-*` extensions (e.g., `x-speakeasy-mcp-ignore`,
 * `x-speakeasy-mcp-scope`) that downstream stages should be able to read. The
 * trust boundary on this opaque content is still the per-operation extraction below.
 */
const OpenApiDocumentSchema = z
  .object({
    openapi: z.string().regex(/^3\./, "OpenAPI 3.x required"),
    info: OpenApiInfoSchema,
    servers: z.array(OpenApiServerSchema).optional(),
    tags: z.array(OpenApiTagSchema).optional(),
    paths: z.record(z.unknown()).default({}),
    components: z.unknown().optional(),
    security: z.array(z.record(z.array(z.string()))).optional()
  })
  .passthrough();

export class OpenApiParseError extends Error {
  constructor(message: string) {
    super(`OpenAPI parse error: ${message}`);
    this.name = "OpenApiParseError";
  }
}

/**
 * Resolve local `#/...` JSON-Pointer $refs against the root document. External / remote
 * refs are intentionally left untouched — following them at intake time is an attack
 * surface (SSRF, supply-chain pull). Callers that need external $refs should pre-bundle
 * with `@apidevtools/swagger-parser`'s `bundle()` and pass the bundled doc here.
 *
 * Cycle handling: a *path-local* expansion stack (Set of ref strings currently being
 * expanded on this branch). When the same $ref re-enters the same expansion path, we
 * return an empty object. Sibling expansions of the same $ref are *not* collapsed, so
 * shared definitions (e.g., the Pet schema referenced from PetPage AND from getPet)
 * are fully resolved each time. Fixes F1 from the Phase-1 review.
 *
 * Prototype-pollution: the accumulator uses `Object.create(null)` and we reject pointer
 * segments matching `__proto__`, `prototype`, `constructor`. Fixes F3.
 */
const DANGEROUS_POINTER_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);

function resolveLocalRefs(root: unknown): unknown {
  const walk = (node: unknown, stack: ReadonlySet<string>): unknown => {
    if (node === null || typeof node !== "object") return node;
    if (Array.isArray(node)) return node.map((v) => walk(v, stack));
    const obj = node as Record<string, unknown>;
    if (typeof obj["$ref"] === "string" && Object.keys(obj).length === 1) {
      const ref = obj["$ref"];
      if (!ref.startsWith("#/")) return obj; // leave external refs untouched
      if (stack.has(ref)) return Object.create(null); // cycle break
      const next = new Set(stack);
      next.add(ref);
      const resolved = resolvePointer(root, ref.slice(2).split("/"));
      if (resolved === undefined) return Object.create(null);
      return walk(resolved, next);
    }
    const out: Record<string, unknown> = Object.create(null);
    for (const [k, v] of Object.entries(obj)) out[k] = walk(v, stack);
    return out;
  };
  return walk(root, new Set<string>());
}

function resolvePointer(root: unknown, segments: string[]): unknown {
  let cur: unknown = root;
  for (const seg of segments) {
    const key = seg.replace(/~1/g, "/").replace(/~0/g, "~");
    if (DANGEROUS_POINTER_SEGMENTS.has(key)) return undefined;
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/**
 * Async variant for callers whose source is a URL or file path. Defers to
 * `@apidevtools/swagger-parser` `bundle()` which resolves external $refs offline.
 * Use this when accepting raw user input; use `parseOpenApi` when the document is
 * already in memory and locally-self-contained.
 */
export async function parseOpenApiFromSource(source: string | object): Promise<CapabilityModel> {
  // Dynamic import keeps swagger-parser out of cold-path costs.
  const mod = await import("@apidevtools/swagger-parser");
  const SwaggerParser = (mod as { default?: unknown; bundle?: unknown }).default ?? mod;
  // The bundle() signature accepts string | object; we wrap in a thin facade.
  const bundled = await (SwaggerParser as { bundle: (s: string | object) => Promise<unknown> }).bundle(
    source as string | object
  );
  return parseOpenApi(bundled);
}

export function parseOpenApi(document: unknown): CapabilityModel {
  const doc = OpenApiDocumentSchema.safeParse(document);
  if (!doc.success) {
    throw new OpenApiParseError(doc.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "));
  }
  // Resolve local $refs against the *original* document so downstream code (sensitivity
  // inference etc.) actually sees property names instead of `{ $ref: ... }` placeholders.
  const dereffed = resolveLocalRefs(document) as Record<string, unknown>;
  const d = OpenApiDocumentSchema.parse(dereffed);

  const operations: Operation[] = [];

  for (const [path, pathItemRaw] of Object.entries(d.paths)) {
    if (!path.startsWith("/")) continue;
    if (!isObject(pathItemRaw)) continue;
    const pathItem = pathItemRaw as Record<string, unknown>;
    const pathParameters = Array.isArray(pathItem.parameters)
      ? pathItem.parameters.map((p) => normalizeParameter(p, path))
      : [];

    for (const method of HTTP_METHODS) {
      const opRaw = pathItem[method];
      if (!isObject(opRaw)) continue;
      const op = opRaw as Record<string, unknown>;

      const opParameters = Array.isArray(op.parameters)
        ? op.parameters.map((p) => normalizeParameter(p, path))
        : [];
      const allParams = mergeParameters(pathParameters, opParameters);

      const operationId =
        typeof op.operationId === "string" && op.operationId.length > 0
          ? op.operationId
          : synthesizeOperationId(method, path);

      const parsed = OperationSchema.parse({
        id: operationId,
        method: method.toUpperCase(),
        path,
        summary: typeof op.summary === "string" ? op.summary : undefined,
        description: typeof op.description === "string" ? op.description : undefined,
        parameters: allParams,
        requestBody: normalizeRequestBody(op.requestBody),
        responses: normalizeResponses(op.responses),
        tags: Array.isArray(op.tags) ? op.tags.filter((t) => typeof t === "string") : [],
        security: Array.isArray(op.security)
          ? (op.security as Array<Record<string, string[]>>)
          : d.security ?? []
      });
      operations.push(parsed);
    }
  }

  // OpenAPI allows relative server URLs ("/api/v3") — they're meant to be
  // resolved against the document URL. We don't have the document URL here
  // (intake is byte-level: it sees the spec, not where it came from), so we
  // synthesise a placeholder absolute URL. The downstream MCP server reads
  // baseUrl as configuration, not as something it calls directly; the
  // operator overrides at deploy time.
  const rawBase = d.servers?.[0]?.url;
  const baseUrl =
    rawBase && /^https?:\/\//i.test(rawBase)
      ? rawBase
      : rawBase
        ? `https://api.example.invalid${rawBase.startsWith("/") ? rawBase : `/${rawBase}`}`
        : "https://api.example.invalid";
  return CapabilityModelSchema.parse({
    apiTitle: d.info.title,
    apiVersion: d.info.version,
    baseUrl,
    operations,
    tags: d.tags ?? []
  });
}

function normalizeParameter(raw: unknown, path: string): Parameter {
  if (!isObject(raw)) {
    throw new OpenApiParseError(`parameter at ${path} is not an object`);
  }
  const r = raw as Record<string, unknown>;
  const location = r.in;
  if (typeof r.name !== "string" || typeof location !== "string") {
    throw new OpenApiParseError(`parameter at ${path} missing name/in`);
  }
  return ParameterSchema.parse({
    name: r.name,
    location,
    required: r.required === true,
    schema: r.schema ?? {},
    description: typeof r.description === "string" ? r.description : undefined
  });
}

function mergeParameters(path: Parameter[], op: Parameter[]): Parameter[] {
  const map = new Map<string, Parameter>();
  for (const p of path) map.set(`${p.location}:${p.name}`, p);
  for (const p of op) map.set(`${p.location}:${p.name}`, p);
  return Array.from(map.values());
}

function normalizeRequestBody(raw: unknown): z.infer<typeof RequestBodySchema> | undefined {
  if (!isObject(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const content = isObject(r.content) ? (r.content as Record<string, unknown>) : undefined;
  if (!content) return undefined;
  const types = Object.keys(content);
  const preferred = types.includes("application/json") ? "application/json" : types[0];
  if (!preferred) return undefined;
  const mediaType = content[preferred];
  const schema = isObject(mediaType) ? (mediaType as Record<string, unknown>).schema ?? {} : {};
  return RequestBodySchema.parse({
    contentType: preferred,
    required: r.required === true,
    schema
  });
}

function normalizeResponses(raw: unknown): z.infer<typeof ResponseSchema>[] {
  if (!isObject(raw)) return [];
  const r = raw as Record<string, unknown>;
  const out: z.infer<typeof ResponseSchema>[] = [];
  for (const [status, value] of Object.entries(r)) {
    if (!isObject(value)) continue;
    const v = value as Record<string, unknown>;
    const content = isObject(v.content) ? (v.content as Record<string, unknown>) : undefined;
    const json = content && isObject(content["application/json"]) ? content["application/json"] : undefined;
    out.push(
      ResponseSchema.parse({
        status,
        description: typeof v.description === "string" ? v.description : undefined,
        schema: json ? (json as Record<string, unknown>).schema : undefined
      })
    );
  }
  return out;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function synthesizeOperationId(method: Method, path: string): string {
  const tokens = path
    .split("/")
    .filter(Boolean)
    .map((seg) => seg.replace(/[{}]/g, ""))
    .map((seg) => seg.replace(/[^a-zA-Z0-9]+/g, "_"));
  return [method, ...tokens].join("_");
}
